import { useCallback, useMemo, useState } from 'react';
import type { ReshareStorySettings, Topic, TopicTypeId } from './types';
import { TopicLogic } from './logic';
import {
  getBrowserTimezone,
  getAvailableTimezones,
  type TimezoneOption,
  normalizeTimezone,
  zonedWallClockToUTC,
} from './timezone';

const toLocalIso = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};

const defaultStart = (): string => {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return toLocalIso(d);
};

const plusOneHour = (localIso: string): string => {
  const d = new Date(localIso);
  d.setHours(d.getHours() + 1);
  return toLocalIso(d);
};

const utcToZonedWallClock = (utcIso: string, tz: string): string => {
  const instant = new Date(utcIso);
  if (isNaN(instant.getTime())) {
    return defaultStart();
  }

  try {
    const zonedString = instant.toLocaleString('sv-SE', {
      timeZone: normalizeTimezone(tz),
      hour12: false,
    });
    return `${zonedString.replace(' ', 'T').slice(0, 19)}`;
  } catch {
    return defaultStart();
  }
};

export interface TopicFormState {
  topicTitle: string;
  setTopicTitle: (value: string) => void;
  topicBody: string;
  setTopicBody: (value: string) => void;
  topicImageData: string | null;
  topicImageDataChanged: boolean;
  setTopicImageData: (value: string | null) => void;
  startTime: string;
  setStartTime: (value: string) => void;
  endTime: string;
  setEndTime: (value: string) => void;
  timezone: string;
  setTimezone: (value: string) => void;
  safeTimezone: string;
  timezones: readonly TimezoneOption[];
  startUTC: Date;
  endUTC: Date;
  endBeforeStart: boolean;
  introLimitCountEnabled: boolean;
  setIntroLimitCountEnabled: (value: boolean) => void;
  introLimitCount: string;
  setIntroLimitCount: (value: string) => void;
  onlyManagersCanShare: boolean;
  setOnlyManagersCanShare: (value: boolean) => void;
  byInviteOnly: boolean;
  setByInviteOnly: (value: boolean) => void;
  type: TopicTypeId;
  setType: (value: TopicTypeId) => void;
  customTemplates?: string;
  setCustomTemplates: (value: string | undefined) => void;
  /** The sharing setup's Settings tab, or null when it was never saved. */
  reshareStorySettings: ReshareStorySettings | null;
  setReshareStorySettings: (value: ReshareStorySettings | null) => void;
  /** Impression goal (#447), as typed; '' means no goal. */
  impressionGoal: string;
  setImpressionGoal: (value: string) => void;
  /** The news byline, as typed; '' leaves it out. */
  byline: string;
  setByline: (value: string) => void;
  /** The topic's location, as typed; '' leaves it out. */
  location: string;
  setLocation: (value: string) => void;
  resetCreateForm: () => void;
  resetFormFromTopic: (topic: Topic) => void;
}

export function useTopicForm(): TopicFormState {
  const [topicTitle, setTopicTitle] = useState('');
  const [topicBody, setTopicBody] = useState('');
  const [topicImageData, setTopicImageDataValue] = useState<string | null>(
    null
  );
  const [topicImageDataChanged, setTopicImageDataChanged] = useState(false);
  const [startTime, setStartTime] = useState<string>(defaultStart);
  const [endTime, setEndTime] = useState<string>(() =>
    plusOneHour(defaultStart())
  );
  const [timezone, setTimezone] = useState<string>(() => getBrowserTimezone());
  const [storedTopicTimezone, setStoredTopicTimezone] = useState<
    string | undefined
  >(undefined);
  const [introLimitCountEnabled, setIntroLimitCountEnabled] = useState(false);
  const [introLimitCount, setIntroLimitCount] = useState('');
  const [onlyManagersCanShare, setOnlyManagersCanShare] = useState(false);
  const [byInviteOnly, setByInviteOnly] = useState(false);
  const [type, setType] = useState<TopicTypeId>('event');
  const [customTemplates, setCustomTemplates] = useState<string | undefined>();
  const [reshareStorySettings, setReshareStorySettings] =
    useState<ReshareStorySettings | null>(null);
  const [impressionGoal, setImpressionGoal] = useState('');
  const [byline, setByline] = useState('');
  const [location, setLocation] = useState('');

  const timezones = useMemo(
    () => getAvailableTimezones(getBrowserTimezone(), storedTopicTimezone),
    [storedTopicTimezone]
  );
  const safeTimezone = normalizeTimezone(timezone);

  const setTopicImageData = useCallback((value: string | null) => {
    setTopicImageDataValue(value);
    setTopicImageDataChanged(true);
  }, []);

  const resetCreateForm = useCallback(() => {
    const nextStart = defaultStart();
    setTopicTitle('');
    setTopicBody('');
    setTopicImageDataValue(null);
    setTopicImageDataChanged(false);
    setStartTime(nextStart);
    setEndTime(plusOneHour(nextStart));
    setTimezone(getBrowserTimezone());
    setStoredTopicTimezone(undefined);
    setIntroLimitCountEnabled(false);
    setIntroLimitCount('');
    setOnlyManagersCanShare(false);
    setByInviteOnly(false);
    setType('event');
    setCustomTemplates(undefined);
    setReshareStorySettings(null);
    setImpressionGoal('');
    setByline('');
    setLocation('');
  }, []);

  const resetFormFromTopic = useCallback((topic: Topic) => {
    const nextTimezone = normalizeTimezone(topic.timezone);
    setTopicTitle(topic.title);
    setTopicBody(topic.body);
    setTopicImageDataValue(topic.imageData ?? null);
    setTopicImageDataChanged(false);
    setStartTime(utcToZonedWallClock(topic.startTime, nextTimezone));
    setEndTime(
      utcToZonedWallClock(topic.endTime ?? topic.startTime, nextTimezone)
    );
    setTimezone(nextTimezone);
    setStoredTopicTimezone(nextTimezone);
    setIntroLimitCountEnabled(topic.introCountLimit != null);
    setIntroLimitCount(
      topic.introCountLimit != null ? String(topic.introCountLimit) : ''
    );
    setOnlyManagersCanShare(topic.onlyManagersCanShare ?? false);
    setByInviteOnly(topic.byInviteOnly ?? false);
    setType(topic.type ?? 'event');
    setCustomTemplates(topic.customTemplates);
    setReshareStorySettings(topic.reshareStorySettings ?? null);
    setImpressionGoal(
      topic.impressionGoal != null ? String(topic.impressionGoal) : ''
    );
    setByline(topic.byline ?? '');
    setLocation(topic.location ?? '');
  }, []);

  const timeRange = useMemo(() => {
    try {
      const startUTC = zonedWallClockToUTC(startTime, safeTimezone);
      const endUTC = zonedWallClockToUTC(endTime, safeTimezone);
      return {
        startUTC,
        endUTC,
        // A type with no end time (news) has nothing to compare.
        endBeforeStart:
          TopicLogic.hasEndTime(type) && endUTC.getTime() <= startUTC.getTime(),
      };
    } catch {
      const startUTC = new Date();
      const endUTC = new Date(startUTC.getTime() + 60 * 60 * 1000);
      return {
        startUTC,
        endUTC,
        endBeforeStart: false,
      };
    }
  }, [endTime, safeTimezone, startTime, type]);

  return {
    topicTitle,
    setTopicTitle,
    topicBody,
    setTopicBody,
    topicImageData,
    topicImageDataChanged,
    setTopicImageData,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    timezone,
    setTimezone,
    safeTimezone,
    timezones,
    startUTC: timeRange.startUTC,
    endUTC: timeRange.endUTC,
    endBeforeStart: timeRange.endBeforeStart,
    introLimitCountEnabled,
    setIntroLimitCountEnabled,
    introLimitCount,
    setIntroLimitCount,
    onlyManagersCanShare,
    setOnlyManagersCanShare,
    byInviteOnly,
    setByInviteOnly,
    type: type,
    setType: setType,
    customTemplates,
    setCustomTemplates,
    reshareStorySettings,
    setReshareStorySettings,
    impressionGoal,
    setImpressionGoal,
    byline,
    setByline,
    location,
    setLocation,
    resetCreateForm,
    resetFormFromTopic,
  };
}
